import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEventBus } from '@vueuse/core'
import axios from 'axios'
import { EventKey } from '@/core/events/event-key'
import useDelete from '@/core/composables/api/useDelete'
import useGet from '@/core/composables/api/useGet'
import usePost from '@/core/composables/api/usePost'
import usePut from '@/core/composables/api/usePut'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { useSnackStore } from '@/stores/snack.store'
import type { Team } from '../models/organization-team'
import type { TeamMutationResult } from './use-organization-teams'

const statusCode = (error: unknown): number | undefined => {
  if (axios.isAxiosError(error)) {
    return error.response?.status
  }
  return undefined
}

export const useOrganizationTeam = (teamId: string) => {
  const { t } = useI18n()
  const snackStore = useSnackStore()
  const teamUpdatedBus = useEventBus(EventKey.teamUpdated)
  const { data, status, load, reload } = useGet<Team>(`admin/teams/${teamId}`)

  const {
    mutate: renameRequest,
    status: renameStatus,
    error: renameError,
  } = usePut<string, { name: string }>(`admin/teams/${teamId}`)

  const {
    mutate: addMembersRequest,
    status: addStatus,
    error: addError,
  } = usePost<void, { memberIds: string[] }>(`admin/teams/${teamId}/members`)

  const removeEndpoint = ref(`admin/teams/${teamId}/members`)
  const {
    remove: removeMemberRequest,
    status: removeStatus,
    error: removeError,
  } = useDelete<void>(removeEndpoint)

  onMounted(() => {
    load()
  })

  const renameTeam = async (name: string): Promise<TeamMutationResult> => {
    await renameRequest({ name: name.trim() })

    if (renameStatus.value === ApiRequestStatus.Success) {
      snackStore.showInfo(t('settings.organizationTeams.snackbar.renameSuccess'))
      await reload()
      return true
    }

    if (statusCode(renameError.value) === 400) {
      return 'invalid-name'
    }
    snackStore.showError(
      statusCode(renameError.value) === 404
        ? t('settings.organizationTeams.snackbar.notFound')
        : t('settings.organizationTeams.snackbar.renameError'),
    )
    return false
  }

  const addMembers = async (memberIds: string[]): Promise<boolean> => {
    await addMembersRequest({ memberIds })

    if (addStatus.value === ApiRequestStatus.Success) {
      snackStore.showInfo(t('settings.organizationTeams.snackbar.addMembersSuccess'))
      teamUpdatedBus.emit({ teamId })
      await reload()
      return true
    }

    const code = statusCode(addError.value)
    if (code === 404) {
      snackStore.showError(t('settings.organizationTeams.snackbar.addMembersNotFound'))
    } else {
      snackStore.showError(t('settings.organizationTeams.snackbar.addMembersError'))
    }
    return false
  }

  const removeMember = async (memberId: string): Promise<boolean> => {
    removeEndpoint.value = `teams/${teamId}/members/${memberId}`
    await removeMemberRequest()

    if (removeStatus.value === ApiRequestStatus.Success) {
      snackStore.showInfo(t('settings.organizationTeams.snackbar.removeMemberSuccess'))
      await reload()
      return true
    }

    snackStore.showError(
      statusCode(removeError.value) === 404
        ? t('settings.organizationTeams.snackbar.notFound')
        : t('settings.organizationTeams.snackbar.removeMemberError'),
    )
    return false
  }

  return {
    team: data,
    status,
    renameTeam,
    addMembers,
    removeMember,
  }
}
